import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';

// Format price based on value: 6 significant digits for $10 and under, 2 decimal places for over $10
const formatPrice = (price) => {
  if (price == null || isNaN(price)) return '';
  const num = parseFloat(price);
  if (num > 10) {
    return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    return num.toLocaleString('en-US', { maximumSignificantDigits: 6 });
  }
};

// Round price to appropriate precision (same as formatPrice but returns a number string without commas)
const roundPrice = (price) => {
  if (price == null || isNaN(price) || price === '') return '';
  const num = parseFloat(price);
  if (num > 10) {
    return num.toFixed(2);
  } else {
    // Round to 6 significant digits
    return parseFloat(num.toPrecision(6)).toString();
  }
};

// Default settings
const DEFAULT_SETTINGS = {
  matchingWindow: 10,
  liquidityParticipants: ['Hedge Funds', 'HFT/Algo Firms'],
  executionMode: 'Fast',
  liquidityProviderType: 'Not Offering Liquidity'
};

// Available liquidity provider types
const LIQUIDITY_PROVIDER_TYPES = [
  { id: 'Not Offering Liquidity', label: 'Not Offering Liquidity', description: 'Not participating as a liquidity provider' },
  { id: 'Hedge Funds', label: 'Hedge Funds', description: 'Institutional investors' },
  { id: 'Foundations', label: 'Foundations', description: 'Protocol treasuries' },
  { id: 'Whales', label: 'Whales', description: 'Large holders' },
  { id: 'HFT/Algo Firms', label: 'HFT/Algo Firms', description: 'High-frequency trading' }
];

// Load settings from localStorage or use defaults
const loadSettings = () => {
  try {
    const saved = localStorage.getItem('tradingSettings');
    return saved ? JSON.parse(saved) : DEFAULT_SETTINGS;
  } catch (error) {
    console.error('Error loading settings:', error);
    return DEFAULT_SETTINGS;
  }
};

function OrderForm({ selectedPair, lastTradePrice, onMatchingRequestSubmit, onMatchingRequestSuccess, wsClient, userId, isConnected = true }) {
  const { token } = useAuth();
  const [side, setSide] = useState('BUY'); // BUY or SELL
  const [maxPrice, setMaxPrice] = useState('');
  const [amount, setAmount] = useState('10.0');
  const [showSettings, setShowSettings] = useState(false);
  const [priceInitialized, setPriceInitialized] = useState(false);

  // Order submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderStatus, setOrderStatus] = useState(null); // 'success' | 'error' | null
  const [orderMessage, setOrderMessage] = useState('');

  // Trading settings state
  const [matchingWindow, setMatchingWindow] = useState(DEFAULT_SETTINGS.matchingWindow);
  const [liquidityParticipants, setLiquidityParticipants] = useState(DEFAULT_SETTINGS.liquidityParticipants);
  const [executionMode, setExecutionMode] = useState(DEFAULT_SETTINGS.executionMode);
  const [liquidityProviderType, setLiquidityProviderType] = useState(DEFAULT_SETTINGS.liquidityProviderType);

  // Track if price input is focused (to disable formatting while editing)
  const [isPriceFocused, setIsPriceFocused] = useState(false);

  // Load settings on mount
  useEffect(() => {
    const settings = loadSettings();
    setMatchingWindow(settings.matchingWindow);
    setLiquidityParticipants(settings.liquidityParticipants);
    setExecutionMode(settings.executionMode);
    setLiquidityProviderType(settings.liquidityProviderType || DEFAULT_SETTINGS.liquidityProviderType);
  }, []);

  // Reset price initialization flag when token changes
  useEffect(() => {
    console.log(`Token changed to ${selectedPair?.coinId}, resetting price...`);
    setPriceInitialized(false);
    setMaxPrice(''); // Clear the old price
  }, [selectedPair?.coinId]);

  // Pre-populate max price with last trade price
  useEffect(() => {
    if (!priceInitialized && lastTradePrice != null) {
      const num = parseFloat(lastTradePrice);
      if (!isFinite(num) || num <= 0) return;
      const rounded = roundPrice(num);
      console.log(`Setting max price to ${rounded} for ${selectedPair?.coinId}`);
      setMaxPrice(rounded);
      setPriceInitialized(true);
    }
  }, [lastTradePrice, priceInitialized, selectedPair?.coinId]);

  // Toggle liquidity participant
  const toggleParticipant = (participant) => {
    setLiquidityParticipants(prev =>
      prev.includes(participant)
        ? prev.filter(p => p !== participant)
        : [...prev, participant]
    );
  };

  // Save settings to localStorage
  const handleSaveSettings = () => {
    const settings = {
      matchingWindow,
      liquidityParticipants,
      executionMode,
      liquidityProviderType
    };
    try {
      localStorage.setItem('tradingSettings', JSON.stringify(settings));

      // Update participant type with registrar via WebSocket
      const participantType = liquidityProviderType !== 'Not Offering Liquidity' ? liquidityProviderType : null;
      if (wsClient && wsClient.readyState === WebSocket.OPEN) {
        console.log('[OrderForm] Updating participantType with registrar:', participantType);
        wsClient.send(JSON.stringify({
          type: 'update_participant_type',
          userId: userId,
          participantType: participantType
        }));
      } else {
        console.log('[OrderForm] WebSocket not connected, participantType will update on next connection');
      }

      setShowSettings(false);
    } catch (error) {
      console.error('Error saving settings:', error);
    }
  };

  // Reset settings to defaults
  const handleResetSettings = () => {
    setMatchingWindow(DEFAULT_SETTINGS.matchingWindow);
    setLiquidityParticipants(DEFAULT_SETTINGS.liquidityParticipants);
    setExecutionMode(DEFAULT_SETTINGS.executionMode);
    setLiquidityProviderType(DEFAULT_SETTINGS.liquidityProviderType);
  };

  // Handle order submission
  const handlePlaceOrder = async () => {
    if (!token) {
      setOrderStatus('error');
      setOrderMessage('You must be logged in to place an order');
      return;
    }

    if (!selectedPair || !selectedPair.coinId) {
      setOrderStatus('error');
      setOrderMessage('Please select a trading pair');
      return;
    }

    setIsSubmitting(true);
    setOrderStatus(null);
    setOrderMessage('');

    // Extract ticker from selectedPair
    const ticker = selectedPair.coinId;
    console.log('Submitting matching request with ticker:', ticker);

    // Store the order data BEFORE making the request
    // This prevents race condition where WebSocket broadcast arrives before HTTP response
    if (onMatchingRequestSubmit) {
      onMatchingRequestSubmit({
        ticker,
        side,
        qty: parseFloat(String(amount).replace(/,/g, '')),
        price: parseFloat(String(maxPrice).replace(/,/g, ''))
      });
      console.log('Order data stored for pending encryption (before fetch)');
    }

    try {
      const response = await fetch('/api/matching/request', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ticker,
          matchingWindow,
          liquidityParticipants,
          executionMode,
          liquidityProviderType
        })
      });

      const data = await response.json();

      if (response.ok) {
        setOrderStatus('success');
        setOrderMessage('Matching request sent successfully!');
        console.log('Matching response:', data);

        // Immediately trigger the progress bar with matching data
        if (onMatchingRequestSuccess) {
          onMatchingRequestSuccess({
            ticker: ticker,
            requestId: data.requestId,
            matchingWindow: matchingWindow,
            startTime: Date.now()
          });
        }

        // Clear form after 3 seconds
        setTimeout(() => {
          setOrderStatus(null);
          setOrderMessage('');
        }, 3000);
      } else {
        setOrderStatus('error');
        // Check for specific error codes
        if (data.code === 'MATCHING_IN_PROGRESS') {
          setOrderMessage('Request for matching already in progress, please try again later');
        } else {
          setOrderMessage(data.error || 'Failed to send matching request');
        }
        // Clear pending order data on failure
        if (onMatchingRequestSubmit) {
          onMatchingRequestSubmit(null);
        }
      }

    } catch (error) {
      console.error('Error placing order:', error);
      setOrderStatus('error');
      setOrderMessage('Network error. Please try again.');
      // Clear pending order data on error
      if (onMatchingRequestSubmit) {
        onMatchingRequestSubmit(null);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="order-form">
        <div className="order-form-header" style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
            <h3 style={{ margin: 0, fontSize: '13px', whiteSpace: 'nowrap' }}>Confidential Matching</h3>
            <span style={{ fontSize: '10px', color: '#666', flex: 1 }}>Use when the order book lacks sufficient liquidity for your trade size</span>
          </div>
          <button
            className="settings-icon-btn"
            onClick={() => setShowSettings(!showSettings)}
            title="Confidential Request for Match Settings"
          >
            ⚙️
          </button>
        </div>

        {/* BUY/SELL Toggle */}
        <div className="side-selector">
          <button
            className={`side-btn buy ${side === 'BUY' ? 'active' : ''}`}
            onClick={() => setSide('BUY')}
          >
            BUY
          </button>
          <button
            className={`side-btn sell ${side === 'SELL' ? 'active' : ''}`}
            onClick={() => setSide('SELL')}
          >
            SELL
          </button>
        </div>

        {/* Max Price Input */}
        <div className="form-group">
          <label>Max Price ({side})</label>
          <div className="input-group">
            <input
              type="text"
              value={isPriceFocused
                ? maxPrice
                : formatPrice(maxPrice)}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, '');
                if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                  setMaxPrice(raw);
                }
              }}
              onFocus={() => setIsPriceFocused(true)}
              onBlur={() => {
                setIsPriceFocused(false);
                // Round to appropriate precision on blur
                if (maxPrice) {
                  setMaxPrice(roundPrice(maxPrice));
                }
              }}
              placeholder="0.00"
            />
            <button className="input-icon-btn">☰</button>
            <span className="currency">USD</span>
          </div>
        </div>

        {/* Amount Input */}
        <div className="form-group">
          <label>Amount</label>
          <div className="input-group">
            <input
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => {
                const raw = e.target.value.replace(/,/g, '');
                if (raw === '' || /^\d*\.?\d*$/.test(raw)) {
                  setAmount(raw);
                }
              }}
              placeholder="0.00"
            />
          </div>
        </div>

        {/* Order Status Message */}
        {orderStatus && (
          <div className={`order-status-message ${orderStatus}`}>
            {orderMessage}
          </div>
        )}

        {/* Submit Button */}
        {liquidityParticipants.length === 0 && (
          <div className="order-status-message error" style={{ marginBottom: 0 }}>
            No liquidity participants selected — open Settings to enable at least one.
          </div>
        )}
        <button
          className={`place-order-btn ${side.toLowerCase()}`}
          disabled={!amount || !maxPrice || isSubmitting || !isConnected || liquidityParticipants.length === 0}
          onClick={handlePlaceOrder}
          style={!isConnected ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          {!isConnected ? 'DISCONNECTED' : isSubmitting ? 'SENDING...' : `PLACE ${side} ORDER`}
        </button>
      </div>

      {/* Trading Settings Modal */}
      {showSettings && (
        <div className="settings-modal-overlay" onClick={() => setShowSettings(false)}>
          <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
            <div className="settings-header">
              <div>
                <h3>Trading Settings</h3>
                <p className="settings-subtitle">Configure your matching preferences</p>
              </div>
              <button className="close-btn" onClick={() => setShowSettings(false)}>×</button>
            </div>

            {/* Matching Window */}
            <div className="settings-section">
              <div className="section-title">
                <span className="toggle-indicator">◉</span>
                Request for Matching Window
              </div>
              <div className="slider-group">
                <input
                  type="range"
                  min="5"
                  max="60"
                  value={matchingWindow}
                  onChange={(e) => setMatchingWindow(Number(e.target.value))}
                  className="slider"
                  style={{
                    background: `linear-gradient(to right, #26a69a 0%, #26a69a ${((matchingWindow - 5) / (60 - 5)) * 100}%, #3a3a3a ${((matchingWindow - 5) / (60 - 5)) * 100}%, #3a3a3a 100%)`
                  }}
                />
                <div className="slider-labels">
                  <span>5s</span>
                  <div className="slider-value">
                    <span>{matchingWindow}</span>
                    <button className="pause-btn">⏸</button>
                    <span>seconds</span>
                  </div>
                  <span>1 min</span>
                </div>
              </div>
            </div>

            {/* Authorized Liquidity Participants */}
            <div className="settings-section">
              <div className="section-title">
                <span className="toggle-indicator">◉</span>
                Authorized Liquidity Participants
              </div>
              <div className="participants-grid">
                <button
                  className={`participant-card ${liquidityParticipants.includes('Hedge Funds') ? 'active' : ''}`}
                  onClick={() => toggleParticipant('Hedge Funds')}
                >
                  <div className="card-title">
                    Hedge Funds {liquidityParticipants.includes('Hedge Funds') ? '✓' : ''}
                  </div>
                  <div className="card-subtitle">Institutional investors</div>
                </button>
                <button
                  className={`participant-card ${liquidityParticipants.includes('Foundations') ? 'active' : ''}`}
                  onClick={() => toggleParticipant('Foundations')}
                >
                  <div className="card-title">
                    Foundations {liquidityParticipants.includes('Foundations') ? '✓' : ''}
                  </div>
                  <div className="card-subtitle">Protocol treasuries</div>
                </button>
                <button
                  className={`participant-card ${liquidityParticipants.includes('Whales') ? 'active' : ''}`}
                  onClick={() => toggleParticipant('Whales')}
                >
                  <div className="card-title">
                    Whales {liquidityParticipants.includes('Whales') ? '✓' : ''}
                  </div>
                  <div className="card-subtitle">Large holders</div>
                </button>
                <button
                  className={`participant-card ${liquidityParticipants.includes('HFT/Algo Firms') ? 'active' : ''}`}
                  onClick={() => toggleParticipant('HFT/Algo Firms')}
                >
                  <div className="card-title">
                    HFT/Algo Firms {liquidityParticipants.includes('HFT/Algo Firms') ? '✓' : ''}
                  </div>
                  <div className="card-subtitle">High-frequency trading</div>
                </button>
              </div>
            </div>

            {/* Execution Mode */}
            <div className="settings-section settings-section--disabled">
              <div className="section-title">
                <span className="toggle-indicator">◉</span>
                Execution Mode
                <span className="coming-soon-badge">Production only</span>
              </div>
              <div className="execution-mode-grid">
                <button className="mode-card" disabled>
                  <div className="mode-title">Fast</div>
                  <div className="mode-subtitle">Optimized for speed</div>
                  <div className="mode-detail">Lower latency execution</div>
                </button>
                <button className="mode-card" disabled>
                  <div className="mode-title">Secure</div>
                  <div className="mode-subtitle">Enhanced verification</div>
                  <div className="mode-detail">Maximum protection</div>
                </button>
              </div>
            </div>

            {/* Liquidity Provider Type */}
            <div className="settings-section">
              <div className="section-title">
                <span className="toggle-indicator">◉</span>
                My Liquidity Provider Type
              </div>
              <div className="provider-type-grid">
                {LIQUIDITY_PROVIDER_TYPES.map((type) => (
                  <button
                    key={type.id}
                    className={`provider-type-card ${liquidityProviderType === type.id ? 'active' : ''}`}
                    onClick={() => setLiquidityProviderType(type.id)}
                  >
                    <div className="card-title">
                      {type.label} {liquidityProviderType === type.id ? '✓' : ''}
                    </div>
                    <div className="card-subtitle">{type.description}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="settings-actions">
              <button className="reset-btn" onClick={handleResetSettings}>RESET</button>
              <button className="save-btn" onClick={handleSaveSettings}>SAVE SETTINGS</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default OrderForm;
